(() => {
  'use strict';
  const watched = new WeakSet();
  // CEBE uses English, including when Shopify's draft preview selects DE.
  // Set the app's locale before it requests its own form and upload messages.
  function useEnglish(root) {
    const app = root.querySelector('[data-b2b-registration-form]');
    if (!app || app.dataset.b2bInitialized === '1') return;
    app.setAttribute('lang', 'en');
    app.setAttribute('data-b2b-locale', 'en');
    const locale = app.querySelector('input[data-b2b-locale]');
    if (locale) locale.value = 'en';
    const definition = app.getAttribute('data-definition-url');
    if (!definition) return;
    const url = new URL(definition, window.location.href);
    url.searchParams.set('locale', 'en');
    app.setAttribute('data-definition-url', url.href);
  }
  function mount(root) {
    const form = root.querySelector('.b2b-registration-block__form');
    const group = form?.querySelector('.b2b-registration-block__custom-fields');
    if (!group || form.dataset.cebeEnhanced === 'true') return;
    const controls = [...group.querySelectorAll('input:not([type=hidden]),select,textarea')];
    const documents = controls.filter(input => input.type === 'file');
    if (!controls.length || documents.length !== 2) return;
    form.dataset.cebeEnhanced = 'true';
    const strings = JSON.parse(root.querySelector('[data-cebe-portal-strings]').textContent);
    const nativeTitle = root.querySelector('[data-b2b-title]');
    if (nativeTitle && strings.formTitle) nativeTitle.textContent = strings.formTitle;
    const progress = root.querySelector('[data-cebe-portal-progress]');
    const status = root.querySelector('[data-cebe-portal-status]');
    const required = controls.filter(input => input.required);
    const validFiles = new Set();
    let pending = false;
    let version = 0;
    let completed = -1;
    const auto = {company_name:'organization',contact_first_name:'given-name',contact_last_name:'family-name',contact_email:'email',country_code:'country',address_line1:'address-line1',address_line2:'address-line2',postal_code:'postal-code',city:'address-level2'};
    controls.forEach(input => {
      const key = input.name.match(/^registration\[([^\]]+)\]$/)?.[1];
      if (auto[key]) input.autocomplete = auto[key];
      if (key === 'contact_email') { input.inputMode = 'email'; input.autocapitalize = 'none'; input.spellcheck = false; }
      if (key === 'postal_code') input.inputMode = 'numeric';
    });
    const vat = group.querySelector('[name="registration[vat_number]"]')?.closest('label');
    const firstName = group.querySelector('[name="registration[contact_first_name]"]')?.closest('label');
    const contactHeading = firstName?.previousElementSibling;
    const taxHeading = vat?.previousElementSibling;
    if (contactHeading?.classList.contains('b2b-registration-block__section-heading') && taxHeading?.classList.contains('b2b-registration-block__section-heading')) {
      group.insertBefore(vat, contactHeading);
      taxHeading.remove();
    }
    [...group.querySelectorAll('.b2b-registration-block__section-heading')].forEach(heading => {
      heading.setAttribute('role','heading');
      heading.setAttribute('aria-level','3');
    });
    const guide = document.createElement('p');
    guide.className = 'cebe-b2b-upload-guide';
    guide.id = root.id + '-upload-guide';
    guide.textContent = strings.upload;
    const docHeading = documents[0].closest('label').previousElementSibling;
    if (docHeading?.classList.contains('b2b-registration-block__section-heading')) docHeading.after(guide);
    const errors = new Map();
    documents.forEach((input,index) => {
      // The app opens this input itself; suppress the label's second activation.
      input.closest('.b2b-registration-block__file-wrap')?.addEventListener('click',event => {
        if (event.target !== input) event.preventDefault();
      },true);
      input.accept = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
      const hint = input.closest('label').querySelector('[data-b2b-help-text]');
      if (hint && /^Maximum upload size: 12 MB\.$/.test(hint.textContent.trim())) hint.hidden = true;
      const error = document.createElement('small');
      error.className = 'cebe-b2b-upload-error';
      error.id = root.id + '-document-error-' + index;
      error.setAttribute('role','alert');
      error.hidden = true;
      input.closest('label').append(error);
      const described = (input.getAttribute('aria-describedby') || '').split(' ').filter(Boolean);
      input.setAttribute('aria-describedby',[...described,guide.id,error.id].join(' '));
      errors.set(input,error);
    });
    function meter() {
      const count = required.filter(input => input.type === 'file' ? validFiles.has(input) : input.value.trim() && input.validity.valid).length;
      if (count === completed) return;
      completed = count;
      progress.max = required.length;
      progress.value = count;
      status.textContent = strings.progress.replace('[count]',String(count)).replace('[total]',String(required.length));
    }
    function feedback(input,message) {
      input.setCustomValidity(message);
      input.toggleAttribute('aria-invalid',Boolean(message));
      if (message) input.setAttribute('aria-invalid','true');
      const error = errors.get(input);
      error.textContent = message;
      error.hidden = !message;
      if (message) validFiles.delete(input);
    }
    async function validateDocuments() {
      const revision = ++version;
      pending = true;
      validFiles.clear();
      documents.forEach(input => feedback(input,input.files?.length ? strings.checking : ''));
      const selected = documents.map(input => ({input,file:input.files?.[0]}));
      const messages = await Promise.all(selected.map(async({file}) => {
        if (!file) return '';
        if (file.size === 0 || file.size > 12 * 1024 * 1024) return strings.size;
        const ext = file.name.split('.').pop().toLowerCase();
        const mime = file.type;
        try {
          const bytes = new Uint8Array(await file.slice(0,8).arrayBuffer());
          const pdf = ext === 'pdf' && (!mime || mime === 'application/pdf') && [37,80,68,70,45].every((value,index) => bytes[index] === value);
          const jpg = ['jpg','jpeg'].includes(ext) && (!mime || mime === 'image/jpeg') && [255,216,255].every((value,index) => bytes[index] === value);
          const png = ext === 'png' && (!mime || mime === 'image/png') && [137,80,78,71,13,10,26,10].every((value,index) => bytes[index] === value);
          return pdf || jpg || png ? '' : strings.format;
        } catch { return strings.format; }
      }));
      if (revision !== version) return;
      const total = selected.reduce((sum,{file}) => sum + (file?.size || 0),0);
      if (total > 12 * 1024 * 1024 && messages.every(message => !message)) {
        const index = selected.findLastIndex(({file}) => Boolean(file));
        messages[index] = strings.total;
      }
      selected.forEach(({input,file},index) => {
        feedback(input,messages[index]);
        if (file && !messages[index]) validFiles.add(input);
      });
      pending = false;
      meter();
    }
    form.addEventListener('input',meter);
    form.addEventListener('change',event => {
      if (documents.includes(event.target)) validateDocuments();
      else meter();
    });
    form.addEventListener('submit',event => {
      if (!pending && documents.every(input => input.validity.valid && (!input.files?.length || validFiles.has(input)))) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      documents.find(input => !input.validity.valid)?.reportValidity();
    },true);
    meter();
  }
  function scan() {
    document.querySelectorAll('[data-cebe-b2b-portal]').forEach(root => {
      if (!watched.has(root)) {
        watched.add(root);
        new MutationObserver(() => {
          useEnglish(root);
          mount(root);
        }).observe(root,{childList:true,subtree:true});
      }
      useEnglish(root);
      mount(root);
    });
  }
  scan();
  document.addEventListener('shopify:section:load',scan);
})();
